import { createContext, useCallback, useContext, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";

/** Minimal history-API router: path + query, Back button works, deep links work. */
interface Route {
  path: string;
  query: URLSearchParams;
}

const Ctx = createContext<{ route: Route; navigate: (to: string, opts?: { replace?: boolean }) => void } | null>(null);

const current = (): Route => ({ path: location.pathname.replace(/\/+$/, "") || "/", query: new URLSearchParams(location.search) });

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState(current);
  useEffect(() => {
    const on = () => setRoute(current());
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const navigate = useCallback((to: string, opts: { replace?: boolean } = {}) => {
    // history.state.depth counts in-app pages, so "Back" never leaves the app unexpectedly.
    const depth = (history.state?.depth ?? 0) as number;
    if (opts.replace) history.replaceState({ depth }, "", to);
    else history.pushState({ depth: depth + 1 }, "", to);
    setRoute(current());
    if (!opts.replace) window.scrollTo({ top: 0 });
  }, []);
  return <Ctx.Provider value={{ route, navigate }}>{children}</Ctx.Provider>;
}

export function useRouter() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRouter outside provider");
  return v;
}

/** Match "/ledger/:id" style patterns. Returns params or null. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const s = path.split("/").filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(":")) params[p[i].slice(1)] = decodeURIComponent(s[i]);
    else if (p[i] !== s[i]) return null;
  }
  return params;
}

export function Link({ to, replace, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean }) {
  const { navigate } = useRouter();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    rest.onClick?.(e);
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to, { replace });
  };
  return <a href={to} {...rest} onClick={onClick} />;
}

/** Go back inside the app if there is history, otherwise to a fallback path. */
export function useBack(fallback = "/") {
  const { navigate } = useRouter();
  return () => {
    if ((history.state?.depth ?? 0) > 0) history.back();
    else navigate(fallback, { replace: true });
  };
}
